import React, { useContext, useEffect, useState } from "react";
import { Container, Modal, ModalBody } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  FaArrowLeft,
  FaCheck,
  FaCheckCircle,
  FaPenNib,
  FaUsers,
} from "react-icons/fa";

import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Dashboard from "../../components/Dashboard/Dashboard";
import { DocumentTypeContext } from "../../context/DocumentContext";
import { UserTypeContext } from "../../context/UserTypeContext";
import { AuthContext } from "../../context/AuthContext";
import { UserToken } from "../../context/hooks/useAuth";
import { useToast } from "../../context/ToastContext";
import { getDocumentSummaryGat } from "../../gateways/DocumentGateway";
import "./SignDocument.css";

const SignDocument = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToast();
  const { getDocumentSign, setInfoSign, getSigners, signers, infoSign } =
    useContext(DocumentTypeContext);
  const { getUsersById, getUserIp, formValuesUsers } =
    useContext(UserTypeContext);
  const { decryptToken } = useContext(AuthContext);

  const [openSignersModal, setOpenSignersModal] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [summaryText, setSummaryText] = useState<string>("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const url = window.location.href;
  const urlObj = new URL(url);
  const isMobile = window.innerWidth <= 768;

  let objUserTk: UserToken;
  if (localStorage.getItem("token")) {
    objUserTk = jwtDecode(localStorage.getItem("token") || "");
  } else {
    objUserTk = {} as UserToken;
  }

  useEffect(() => {
    let token = urlObj.searchParams.get("token");
    const documentId = urlObj.searchParams.get("documentId");
    const toPainel = urlObj.searchParams.get("sign");

    token = decodeURIComponent(token || "").replace(/ /g, "+");

    let errorShown = false;

    const showSingleError = () => {
      if (!errorShown) {
        showToast(
          "error",
          "Contrato expirado ou inválido. Entre em contato com o proprietário do documento.",
        );
        errorShown = true;
      }
    };

    const getDocument = async () => {
      if (token) {
        setPageLoading(true);
        const result = await decryptToken(token);

        if (result?.data?.data) {
          const { documentId, userId } = result.data.data;
          setInfoSign(result.data.data);

          try {
            await Promise.all([
              getDocumentSign(documentId, true),
              getSigners(documentId, true),
              getUsersById(userId, true),
            ]);
          } catch (error) {
            showSingleError();
          }

          await getUserIp();
        } else {
          showSingleError();
        }
        setPageLoading(false);
      }
    };

    const getDocumentById = async () => {
      if (documentId) {
        setPageLoading(true);
        try {
          await Promise.all([
            getDocumentSign(documentId, true),
            getSigners(documentId, true),
            getUsersById(objUserTk.UserId, true),
          ]);

          if (toPainel) {
            setInfoSign({ documentId, userId: objUserTk.UserId });
            await getUserIp();
          }
        } catch (error) {
          showSingleError();
        }
        setPageLoading(false);
      }
    };

    if (token) {
      getDocument();
    } else if (documentId) {
      getDocumentById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentSigner = signers?.find?.(
    (x: any) => x.name === formValuesUsers.email,
  );
  const hasSigned = !!currentSigner?.hasSigned;
  const totalSigners = signers?.length || 0;
  const signedCount =
    signers?.filter?.((s: any) => s.hasSigned)?.length || 0;
  const progressPct = totalSigners
    ? Math.round((signedCount / totalSigners) * 100)
    : 0;

  const resolveDocumentId = () => {
    const docIdFromUrl = urlObj.searchParams.get("documentId");
    if (docIdFromUrl) return docIdFromUrl;
    if (infoSign?.documentId) return infoSign.documentId;
    return null;
  };

  const handleOpenSummary = async () => {
    const documentId = resolveDocumentId();

    if (!documentId) {
      showToast("error", "Documento não encontrado para gerar o resumo.");
      return;
    }

    try {
      setOpenSummaryModal(true);
      setSummaryLoading(true);
      setSummaryText("");
      const output = await getDocumentSummaryGat(documentId);
      setSummaryText(output || "Nenhum resumo retornado pela IA.");
    } catch (error) {
      console.error(error);
      setSummaryText("Erro ao gerar resumo do documento.");
      showToast("error", "Erro ao gerar resumo do documento.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const goToSignaturePage = () => {
    if (hasSigned) {
      navigateWithParams("/admin/sign-document/concluido");
      return;
    }
    navigateWithParams("/admin/sign-document/assinar");
  };

  const navigateWithParams = (pathname: string) => {
    const params = new URLSearchParams(location.search);
    navigate({
      pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  if (pageLoading) {
    return (
      <div className="signdoc-loading">
        <div className="signdoc-loading__spinner" />
        <span className="signdoc-loading__text">Carregando documento...</span>
      </div>
    );
  }

  return (
    <>
      <LoadingSpinner />

      {/* ===== Topbar ===== */}
      <header className="signdoc-topbar">
        <button
          type="button"
          className="signdoc-topbar__back"
          onClick={() => window.history.back()}
          aria-label="Voltar"
        >
          <FaArrowLeft />
          {!isMobile && <span>Voltar</span>}
        </button>

        <div className="signdoc-topbar__center">
          <span className="signdoc-topbar__eyebrow">
            {hasSigned ? "Documento assinado" : "Aguardando sua assinatura"}
          </span>
          <strong className="signdoc-topbar__title">
            {infoSign?.documentName || "Documento para assinatura"}
          </strong>
        </div>

        <div className="signdoc-topbar__actions">
          {/* Botão resumo IA oculto — modal e lógica permanecem no código
          <button
            type="button"
            className="signdoc-topbar__btn signdoc-topbar__btn--ghost"
            onClick={handleOpenSummary}
            title="Resumo gerado por IA"
          >
            <FaMagic />
            {!isMobile && <span>Resumo IA</span>}
          </button>
          */}
          <button
            type="button"
            className="signdoc-topbar__btn signdoc-topbar__btn--ghost"
            onClick={() => setOpenSignersModal(true)}
            title="Ver signatários"
          >
            <FaUsers />
            {!isMobile && <span>Signatários</span>}
          </button>
          <button
            type="button"
            className={`signdoc-topbar__btn signdoc-topbar__btn--primary ${
              hasSigned ? "signdoc-topbar__btn--success" : ""
            }`}
            onClick={goToSignaturePage}
            disabled={false}
          >
            {hasSigned ? (
              <>
                <FaCheckCircle />
                {!isMobile && <span>Já assinado</span>}
              </>
            ) : (
              <>
                <FaPenNib />
                <span>Assinar</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* ===== Status banner ===== */}
      <div
        className={`signdoc-banner ${
          hasSigned ? "signdoc-banner--success" : ""
        }`}
      >
        <div className="signdoc-banner__inner">
          <div className="signdoc-banner__icon">
            {hasSigned ? <FaCheck /> : <FaPenNib />}
          </div>
          <div className="signdoc-banner__text">
            <strong>
              {hasSigned
                ? "Você já assinou este documento."
                : "Confira o conteúdo antes de assinar."}
            </strong>
            <span>
              {hasSigned
                ? `${signedCount} de ${totalSigners} signatários já assinaram. Quando todos concluírem, o documento final será enviado por e-mail.`
                : "Leia com atenção. Sua assinatura tem validade jurídica."}
            </span>
          </div>
          <div className="signdoc-banner__progress" aria-hidden>
            <span className="signdoc-banner__progress-pct">{progressPct}%</span>
            <div className="signdoc-banner__progress-track">
              <div
                className="signdoc-banner__progress-bar"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ===== Layout: signers + document ===== */}
      <div className="signdoc-content">
        {!isMobile && (
          <aside className="signdoc-signers">
            <div className="signdoc-signers__header">
              <h3 className="signdoc-signers__title">Signatários</h3>
              <span className="signdoc-signers__count">
                {signedCount}/{totalSigners}
              </span>
            </div>
            {signers?.length > 0 ? (
              signers.map((signer: any, index: number) => (
                <SignerItem
                  key={`${signer.email || signer.name}-${index}`}
                  signer={signer}
                  isMe={signer.name === formValuesUsers.email}
                />
              ))
            ) : (
              <p className="signdoc-signers__empty">
                Nenhum signatário encontrado.
              </p>
            )}
          </aside>
        )}

        <section className="signdoc-document">
          <Container className="mt-3">
            <Dashboard />
          </Container>
        </section>
      </div>

      {/* ===== Modal Signatários (mobile) ===== */}
      <Modal
        isOpen={openSignersModal}
        toggle={() => setOpenSignersModal(false)}
        className="signdoc-modal"
        centered
      >
        <div className="signdoc-modal__header">
          <h4 className="signdoc-modal__title">Signatários</h4>
        </div>
        <ModalBody className="signdoc-modal__body">
          {signers?.length > 0 ? (
            signers.map((signer: any, index: number) => (
              <SignerItem
                key={`m-${signer.email || signer.name}-${index}`}
                signer={signer}
                isMe={signer.name === formValuesUsers.email}
              />
            ))
          ) : (
            <p style={{ textAlign: "center", margin: 0 }}>
              Nenhum signatário encontrado.
            </p>
          )}
        </ModalBody>
      </Modal>

      {/* ===== Modal Resumo IA ===== */}
      <Modal
        isOpen={openSummaryModal}
        toggle={() => setOpenSummaryModal(false)}
        className="signdoc-modal"
        centered
      >
        <div className="signdoc-modal__header">
          <h4 className="signdoc-modal__title">Resumo do Documento (IA)</h4>
        </div>
        <ModalBody
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            padding: "16px 20px",
            whiteSpace: "pre-wrap",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {summaryLoading ? (
            <div className="signdoc-modal__loader">
              <div className="signdoc-modal__loader-spinner" />
              <span>Gerando resumo do documento...</span>
            </div>
          ) : (
            <p style={{ margin: 0 }}>{summaryText}</p>
          )}
        </ModalBody>
      </Modal>

      {/* ===== Floating CTA (mobile) ===== */}
      {isMobile && !hasSigned && (
        <button
          type="button"
          className="signdoc-fab"
          onClick={goToSignaturePage}
        >
          <FaPenNib />
          Assinar Documento
        </button>
      )}
    </>
  );
};

interface SignerItemProps {
  signer: any;
  isMe?: boolean;
}

const SignerItem = ({ signer, isMe }: SignerItemProps) => {
  const initial = (signer.signerName || signer.name || "?")
    .toString()
    .charAt(0)
    .toUpperCase();
  const signed = !!signer.hasSigned;

  return (
    <div
      className={`signdoc-signer ${signed ? "signdoc-signer--signed" : ""} ${
        isMe ? "signdoc-signer--me" : ""
      }`}
    >
      <div className="signdoc-signer__avatar">{initial}</div>
      <div className="signdoc-signer__info">
        <span className="signdoc-signer__name" title={signer.signerName}>
          {signer.signerName || signer.name}
          {isMe && <span className="signdoc-signer__me-tag">você</span>}
        </span>
        <span className="signdoc-signer__email" title={signer.email}>
          {signer.email || signer.name}
        </span>
      </div>
      <span
        className={`signdoc-signer__status ${
          signed ? "is-signed" : "is-pending"
        }`}
      >
        {signed ? (
          <>
            <FaCheck /> Assinado
          </>
        ) : (
          "Pendente"
        )}
      </span>
    </div>
  );
};

export default SignDocument;
